/* eslint-disable ts/naming-convention */
import { useDebounceFn, useLocalStorageState, useThrottleFn } from 'ahooks'
import { flatten } from 'flat'
import { logger } from '@loc/helpers/src/logger/client'

import { sa } from './helpers/sa'

import type { $Enums } from '@prisma/client'
import type { TUseSubLog } from '@/contexts/SiteSpecificContext/hooks/subLog'

import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { sha256 } from '@/helpers/sha256'

export interface IEcommerce {
  currency: string
  value: number
  appName: $Enums.SubLogAppName
  payment_platform?: string
  transaction_id?: string
  origin_url?: string
  coupon?: string
  renewal?: number | string
  /** 剩余额度 */
  remaining_usage?: Record<string, string | number>
  origin_referrer?: string
  items: {
    item_id: string
    item_name: string
    /** 数量 */
    quantity: number
    price: number
    /** 产品所属名称即appName */
    item_category: $Enums.SubLogAppName
    /** 周期 Annually | Monthly */
    item_category2: 'Annually' | 'Monthly'
    /** customer id */
    item_category5?: string
  }[]
}

export interface ITriggerSignUp {
  buttonText: string
  appName?: $Enums.SubLogAppName
  location?: 'modal'
}

export interface ITriggerSignIn {
  buttonText: string
  appName?: $Enums.SubLogAppName
  location?: 'modal'
}

export interface ILoginOrSignUpProps {
  method: 'Google' | 'email'
  user_id: string
  email_sha256: string
  location?: 'modal'
  origin_url: string
  origin_referrer?: string
}

export interface IUseTool {
  model: string
  tool_name: string
  value?: number
  application: 'Web'
  application_category: //
  // Web
  'Website' | 'Dashboard'
  page_url?: string
  /* 类目一 */
  category1: 'Bypass'
  /* 类目二 指的是toolType */
  category2: string
  generates?: Record<string, any>[]
  plan: TUseSubLog
}

export const UserSignUpSessionStorageName = 'user-sign-up'

export function useAnalyticsData() {
  const url = useUrl()
  const urlOrigin = url.origin

  const handleIdentify = ({
    userId,
    emailSha256,
  }: {
    userId: string
    emailSha256: string
  }) => {
    sa.updateUserId(userId)
    sa.updateEmail(emailSha256)
  }

  const { isSignIn, email, userId: sessionUserId } = useSessionInfo()

  const getUserParams = async (props?: {
    userEmail?: string
    userId?: string
  }) => {
    const { userEmail, userId } = props || {}
    if (userEmail && userId) {
      return {
        email_sha256: await sha256(userEmail.toLowerCase()),
        user_id: userId,
      }
    }
    if (isSignIn && email) {
      const email_sha256 = await sha256(email.toLowerCase())
      return {
        email_sha256,
        user_id: sessionUserId,
      }
    }
    return {}
  }

  const trackTriggerSignUp = ({ buttonText, ...rest }: ITriggerSignUp) => {}

  const handleLoginOrSignUpProps = (props: ILoginOrSignUpProps) => {
    const { origin_url, ...rest } = props

    if (!origin_url) {
      return {
        ...rest,
      }
    }
    try {
      const origin_host = new URL(origin_url, urlOrigin).host
      const origin_path = new URL(origin_url, urlOrigin).pathname
      return {
        ...rest,
        origin_url,
        origin_host,
        origin_path,
      }
    } catch (error) {
      logger.error(error)
      return {
        ...rest,
      }
    }
  }

  const [userIsSignUp, setUserIsSignUp] = useLocalStorageState<
    string | undefined
  >(UserSignUpSessionStorageName, {
    defaultValue: '',
  })

  const onChangeUserIsSignUp = (value?: string) => {
    setUserIsSignUp(value)
  }

  const trackSignUp = (props: ILoginOrSignUpProps & { timestamp: Date }) => {
    const { timestamp, ...rest } = props
    setUserIsSignUp('true')
    const params = handleLoginOrSignUpProps(rest)
    sa.track('sign_up', rest)
    handleIdentify({
      userId: props.user_id,
      emailSha256: props.email_sha256,
    })
  }

  const trackTriggerSignIn = ({
    buttonText,
    appName,
    location,
  }: ITriggerSignIn) => {}

  const trackSignIn = (props: ILoginOrSignUpProps) => {
    const params = handleLoginOrSignUpProps(props)
    handleIdentify({
      userId: props.user_id,
      emailSha256: props.email_sha256,
    })
    sa.track('login', params)
  }

  const trackTriggerUpgrade = ({
    buttonText,
    appName,
    location,
  }: {
    buttonText?: string
    location?: 'modal' | 'left side'
    appName: $Enums.SubLogAppName
  }) => {}

  const handleEcommerce = (ecommerce: IEcommerce) => {
    const flattenEcommerce = flatten(ecommerce) as Record<string, any>
    if (ecommerce?.origin_url) {
      try {
        const origin_host = new URL(ecommerce.origin_url, urlOrigin).host
        const origin_path = new URL(ecommerce.origin_url, urlOrigin).pathname
        return {
          ...ecommerce,
          ...flattenEcommerce,
          origin_host,
          origin_path,
        }
      } catch (error) {
        logger.error(error)
        return {
          ...ecommerce,
          ...flattenEcommerce,
        }
      }
    }
    return {
      ...ecommerce,
      ...flattenEcommerce,
    }
  }

  const trackBeginCheckout = async ({
    ecommerce,
  }: {
    ecommerce: IEcommerce
  }) => {
    const userParams = await getUserParams()

    sa.track('begin_checkout', { ecommerce, ...userParams })
  }

  const [upgradeIdsLocalStorage, setUpgradeIds] = useLocalStorageState<
    string[]
  >('upgrade-ids', {
    defaultValue: [],
  })

  const trackUpgrade = ({
    ecommerce,
    timestamp,
  }: {
    ecommerce: IEcommerce
    timestamp: Date
  }) => {
    const upgradeIds = upgradeIdsLocalStorage || []
    const onTrack = () => {}
    if (
      ecommerce?.transaction_id &&
      !upgradeIds.includes(ecommerce.transaction_id)
    ) {
      setUpgradeIds([...upgradeIds, ecommerce.transaction_id])
      onTrack()
    } else if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
      // 非正式环境继续上报，方便调试
      onTrack()
    }
  }

  const [purchaseIdsLocalStorage, setPurchaseIds] = useLocalStorageState<
    string[]
  >('purchase-ids', {
    defaultValue: [],
  })

  const trackPurchase = ({
    ecommerce,
    userId,
    timestamp,
    userEmail,
  }: {
    ecommerce: IEcommerce
    userId?: string
    timestamp: Date
    userEmail?: string
  }) => {
    const purchaseIds = purchaseIdsLocalStorage || []

    const onTrack = async () => {
      const userParams = await getUserParams({
        userId,
        userEmail,
      })
      sa.track('purchase', {
        ecommerce,
        ...userParams,
        user_id: userId,
      })
    }

    if (
      ecommerce?.transaction_id &&
      !purchaseIds.includes(ecommerce.transaction_id)
    ) {
      setPurchaseIds([...purchaseIds, ecommerce.transaction_id])
      onTrack()
    } else if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
      // 非正式环境继续上报，方便调试
      onTrack()
    }
  }

  const trackUnsubscribeComplete = ({
    ecommerce,
    reasons,
    description,
    timestamp,
  }: {
    ecommerce: IEcommerce
    reasons: Record<string, string>
    description: string
    timestamp: Date
  }) => {}

  // 上报点击按钮
  const trackClickButton = ({ description }: { description: string }) => {
    sa.track('click_button', {
      description,
    })
  }

  const { mutate } = api.music.statAudio.useMutation({
    trpc: {
      context: {
        ignoreError: true,
      },
    },
  })

  /** 上报用户的各种操作行为 */
  const { run: trackUserActivityDebounce } = useDebounceFn(
    (props: Parameters<typeof mutate>[0]) => {
      mutate(props)
    },
    {
      wait: 3000,
    },
  )

  const { run: trackUserActivityThrottle } = useThrottleFn(
    (props: Parameters<typeof mutate>[0]) => {
      mutate(props)
    },
    {
      wait: 8000,
    },
  )

  return {
    handleIdentify,
    trackTriggerSignUp,
    handleLoginOrSignUpProps,
    trackSignUp,
    trackTriggerSignIn,
    trackSignIn,
    trackUnsubscribeComplete,
    trackBeginCheckout,
    trackUpgrade,
    trackPurchase,
    trackTriggerUpgrade,
    trackClickButton,
    userIsSignUp,
    onChangeUserIsSignUp,
    trackUserActivityDebounce,
    trackUserActivityThrottle,
  }
}
